import * as React from 'react'
import Collection from './collection/Collection'
import Pagination from './Pagination'
import FiltersNav from './FiltersNav'
import { MessageBlock, MessageTypes, Spinner } from 'DVWebNodeUI'
import * as styles from './Content.module.scss'
import * as cn from 'classnames'
import ATFMarker from './metric/ATFMarker'
import CFMarker from './metric/CFMarker'
import AsyncCallsMarkers from './metric/AsyncCallsMarkers'
import EPGButton from './EPGButton'
import VideoWizard from './video-wizard/VideoWizard'
import { StorefrontState } from '../containers/Storefront'

interface ContentProps extends StorefrontState {
  isPhablet: boolean
  loadFilteredContent: Function
  hasExperimentalFilters?: boolean
  isLoading?: boolean
  fetch: Function
  enrichCollection?: Function
  enrichSingleItem?: Function
  canLazyLoad?: boolean
  removeItem?: Function
  updateChannelItem?: Function
  loadMoreContent?: Function
  asyncCallsScopes?: string[]
  storefrontReloadPageHandler?: Function
}

interface ContentState {
  truncateCollectionList: boolean
  vwEnabled: boolean
  isFrozen: boolean
  storefrontScrollingDisabled: boolean
}

export interface CollectionSliceProps extends ContentProps {
  offset: number
  length?: number
}

const CollectionSlice = (props: CollectionSliceProps) => {
  if (!props.collections) {
    return null
  }
  const endPosition = props.length !== undefined ? props.offset + props.length : props.collections.length
  const slice = props.collections.slice(props.offset, endPosition)
  return <>
    {slice.map((collection, k) => (
      <Collection
        // Default to use the index if there's no webUid for the collection
        key={collection.webUid || k}
        {...collection}
        isPhablet={props.isPhablet}
        fetch={props.fetch}
        hoverClass={props.hoverClass}
        featureSwitches={props.featureSwitches}
        isLoading={props.isLoading}
        isFrozen={props.isFrozen}
        collectionKey={props.offset + k}
        enrichCollection={props.enrichCollection}
        enrichSingleItem={props.enrichSingleItem}
        canLazyLoad={props.canLazyLoad}
        removeItem={props.removeItem}
        showBlueBars={props.showBlueBars}
        updateChannelItem={props.updateChannelItem}
        canLivePlayback={props.canLivePlayback}
        hasAltTheme={props.hasAltTheme}
        formFactor={props.formFactor}
      />
    ))}
  </>
}

interface BelowFiltersNavProps extends ContentProps {
  isSplit: boolean
  length?: number
}

const BelowFiltersNav = (props: BelowFiltersNavProps) => {
  const atfPosition = 3
  const superheroLength = props.isSplit ? 0 : 1
  return (
    <div>
      <CollectionSlice {...props} offset={0} length={superheroLength}
        />
      <div className={styles.belowHero}>
        {
          (!props.isLoading && props.hasHalloweenTheme) &&
            <div className={cn(styles.halloweenBelowHero,
               { [styles.mobileHalloweenBelowHero]: props.isPhablet })}/>
        }
        <div>
          {props.isLoading &&
            <div className={cn(styles.BelowFiltersNavSpinner, 'tst-filtersnav-loader',
              { 'avu-section-alt': props.hasAltTheme })}>
              <Spinner />
            </div>}
          <CollectionSlice {...props} offset={1} length={atfPosition} />
          <ATFMarker />
          <CollectionSlice {...props} offset={1 + atfPosition} length={props.length ? props.length - (atfPosition - superheroLength) : undefined} />
          <Pagination {...props.pagination} loadMoreContent={props.loadMoreContent}
            hasAltTheme={props.hasAltTheme} />
          <CFMarker />
          <AsyncCallsMarkers scopes={props.asyncCallsScopes} />
        </div>
      </div>
    </div>
  )
}

class Content extends React.Component<ContentProps, ContentState> {
  constructor(props: ContentProps) {
    super(props)

    // TODO(PVWebXPL-116): Add local storage flag here
    const vwEnabled = Boolean(this.props.featureSwitches && this.props.featureSwitches.vwEnabled)

    this.state = {
      truncateCollectionList: Boolean(props.featureSwitches.delayCarouselRendering),
      vwEnabled: vwEnabled,
      isFrozen: vwEnabled,
      storefrontScrollingDisabled: false
    }
  }

  loadMoreWithTruncation() {
    if (this.state.truncateCollectionList) {
      this.setState({truncateCollectionList: false})
    } else {
      this.props.loadMoreContent && this.props.loadMoreContent()
    }
  }

  unfreeze() {
    this.setState({
      isFrozen: false,
      storefrontScrollingDisabled: false
    })
  }

  disableStorefrontScrolling() {
    this.setState({ storefrontScrollingDisabled: true })
  }

  render() {
    const hasSuperheroInFirstSlot = this.props.collections &&
      this.props.collections.length > 0 &&
      this.props.collections[0] &&
      this.props.collections[0].collectionType === 'SuperHero'
    const hasFilterNav = this.props.facetedNav &&
      this.props.facetedNav.filter &&
      this.props.facetedNav.filter.values
    const showMessage = Boolean(!this.props.hasFailed && this.props.customerMessage)
    const isSplit = Boolean(hasSuperheroInFirstSlot && (hasFilterNav || Boolean(this.props.epgIngress)))
    const padEpgButton = this.props.featureSwitches && this.props.featureSwitches.epgIngressHasExtraPadding
    const hasHalloweenTheme = this.props.featureSwitches &&
      this.props.featureSwitches.halloweenTheme

    return (
      <div className={this.state.vwEnabled && this.state.storefrontScrollingDisabled ? styles.StorefrontScrollingDisabled : ''}>
        {this.state.vwEnabled && <VideoWizard
          context={this.props.context}
          onComplete={() => this.unfreeze()}
          storefrontReloadPageHandler={this.props.storefrontReloadPageHandler}
          logger={this.props.logger}
          onShow={() => this.disableStorefrontScrolling()}
        />}
        {showMessage && <MessageBlock type={MessageTypes.warning} heading={null} >
          {this.props.customerMessage}
        </MessageBlock>}
        {isSplit && <CollectionSlice
          {...this.props}
          offset={0}
          length={1}
          isPhablet={this.props.isPhablet}
          isFrozen={this.state.isFrozen}
        />}
        {!global.__NATIVEAPP__ && <FiltersNav
          {...this.props.facetedNav}
          loadFilteredContent={this.props.loadFilteredContent}
          hasExperimentalFilters={this.props.hasExperimentalFilters}
          hasAltTheme={this.props.hasAltTheme}
        />}
        {!global.__NATIVEAPP__ && <EPGButton {...this.props.epgIngress} isPhablet={this.props.isPhablet} requiresExtraPadding={padEpgButton} />}
        <BelowFiltersNav {...this.props} isFrozen={this.state.isFrozen} hasHalloweenTheme={hasHalloweenTheme} loadMoreContent={this.loadMoreWithTruncation.bind(this)} isSplit={isSplit} length={this.state.truncateCollectionList ? 6 : undefined} />
      </div>
    )
  }
}

export default Content
